import { FC, useState } from "react";
import {
  ITabs,
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiModal,
  MuiTabPanel,
  MuiTabs,
} from "../../../../components";
import { useSiteCore } from "../../../../hooks/useSiteCore";

import { InformationBadge } from "../../../../common/informationBadge/informationBadge";
import { replaceHashWithValue } from "../../../../helpers/utils";
import { PAGE_IDS } from "../../../../helpers/constants";
import { clientSpecificContentKeys } from "../../siteCore/SiteCoreKeys";
import { ViewClientSpecificContentWrapper } from "./ViewClientSpecificContent.style";
import { ViewResource } from "./viewResource/ViewResource";
import { ViewProgramTemplates } from "./viewProgramTemplates/ViewProgramTemplates";
import { ClientSpecificContent } from "../../modelsAndSchemas/Models";
import { COMPONENT_IDS } from "../../../../helpers/constants/componentIds";

interface ViewClientSpecificContentProps {
  show: boolean;
  onClose: () => void;
  isEditable?: boolean;
  clientSpecificContentData: ClientSpecificContent;
  onClientSpecificContentEdit?: (groupId: string) => void;
}
const pageId = PAGE_IDS.VIEW_CLIENT_SPECIFIC_CONTENT;
export const ViewClientSpecificContent: FC<ViewClientSpecificContentProps> = ({
  show,
  onClose,
  isEditable = true,
  clientSpecificContentData,
  onClientSpecificContentEdit,
}) => {
  const [selectedTab, setSelectedTab] = useState(0);

  const { commonLabels, fetchLabel } = useSiteCore();

  const {
    clientSpecificContentTitle,
    resourcesTab,
    programTemplatesTab,
    groupName,
    viewClientSpecificContentHeading,
    editGroupButtontext,
    viewClientSpecificContentNote,
  } = clientSpecificContentKeys;

  const getLabel = (key: string) => {
    return fetchLabel(clientSpecificContentTitle, key);
  };

  const handleEditClientSpecificContent = () => {
    onClientSpecificContentEdit?.(
      clientSpecificContentData.clientResourceGroupId,
    );
  };

  const tabIdResources = `${pageId}-tab1`;
  const tabIdProgramTemplates = `${pageId}-tab2`;

  const tabs: ITabs[] = [
    {
      id: tabIdResources,
      label: getLabel(resourcesTab),
    },
    {
      id: tabIdProgramTemplates,
      label: getLabel(programTemplatesTab),
    },
  ];

  const onTabChange = (event: React.ChangeEvent<{}>, value: number) => {
    setSelectedTab(value);
  };

  return (
    <MuiModal
      id={COMPONENT_IDS.COMMON_MODAL}
      open={show}
      onClose={onClose}
      title={replaceHashWithValue(
        getLabel(viewClientSpecificContentHeading),
        `${clientSpecificContentData.clientResourceGroupName}`,
      )}
      showPrimaryButton={false}
      secondaryButtonTitle={commonLabels.close}
      onSecondaryButtonClick={onClose}
      tertiaryButtonTitle={isEditable ? getLabel(editGroupButtontext) : ""}
      onTertiaryButtonClick={handleEditClientSpecificContent}
      hasLargeContent
      stackButtonsOnMobile={true}
    >
      <ViewClientSpecificContentWrapper>
        <InformationBadge message={getLabel(viewClientSpecificContentNote)} />
        <MuiGrid size={{ md: 4, sm: 6, xs: 12 }}>
          <MuiBox className="group-name-container">
            <MuiDoubleLabel
              label={getLabel(groupName)}
              value={clientSpecificContentData.clientResourceGroupName}
              isValueWithEllipsis
            />
          </MuiBox>
        </MuiGrid>
        <MuiTabs
          tabs={tabs}
          value={selectedTab}
          onChange={onTabChange}
          tabsTestIds={[tabIdResources, tabIdProgramTemplates]}
        >
          <MuiTabPanel
            panelId={`${pageId}-panel1`}
            value={selectedTab}
            index={0}
            ariaLabelledById={tabIdResources}
          >
            <ViewResource
              resources={clientSpecificContentData.resources ?? []}
            />
          </MuiTabPanel>
          <MuiTabPanel
            panelId={`${pageId}-panel2`}
            value={selectedTab}
            index={1}
            ariaLabelledById={tabIdProgramTemplates}
          >
            <ViewProgramTemplates
              programTemplates={
                clientSpecificContentData.programTemplates ?? []
              }
            />
          </MuiTabPanel>
        </MuiTabs>
      </ViewClientSpecificContentWrapper>
    </MuiModal>
  );
};
